import { useMemo } from "react"
import { useAuthStore } from "@/features/auth/store/authStore"
import type { FacebookPageInfo } from "../types/social-publish.types"
import { useFacebookPages } from "./useFacebookPages"

export interface InstagramConnectionState {
  isConnected: boolean
  pages: FacebookPageInfo[]
  /** True while the pages query is doing its first fetch (connection unknown). */
  isLoading: boolean
}

/**
 * Lightweight hook to check if the current user has a connected Instagram
 * Business account. Wraps `useFacebookPages` and derives connection status
 * from whether any page has a non-null `igBusinessAccountId`.
 *
 * Pass `enabled` (e.g. a modal's `open` state) so the underlying Facebook-pages
 * fetch only runs when the share UI is actually visible. This matters because
 * ShareModal is mounted once per card in list views — leaving it enabled by
 * auth alone fired one /accounts/facebook/pages request per card.
 */
export function useInstagramConnection(enabled = true): InstagramConnectionState {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated())
  const { data: pages, isLoading } = useFacebookPages(enabled && isAuthenticated)

  return useMemo(() => {
    const pageList = pages ?? []
    const hasIgBusiness = pageList.some(p => !!p.igBusinessAccountId)
    return {
      isConnected: hasIgBusiness,
      pages: pageList,
      isLoading,
    }
  }, [pages, isLoading])
}
